import React, { useCallback, useContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { action } from 'mobx';
import { Size } from '../core/enum';
import { RecordCachedType } from '../data-set/enum';
import { $l } from '../locale-context';
import TableContext from './TableContext';
import Tooltip from '../tooltip';
import Button from '../button/Button';
import ObserverSelect from '../select/Select';
import { FuncType } from '../button/enum';
import { getCount } from './utils';
const { Option } = ObserverSelect;
export const cachedTypeIntlMap = {
    [RecordCachedType.selected]: 'cached_type_selected',
    [RecordCachedType.add]: 'cached_type_created',
    [RecordCachedType.update]: 'cached_type_updated',
    [RecordCachedType.delete]: 'cached_type_destroyed',
};
const CachedTips = function CachedTip(props) {
    const { dataSet, tableStore, prefixCls, } = props;
    let { recordCachedType, defaultRecordCachedType } = tableStore;
    const options = [];
    const { selected: { length: selectedLength }, cachedCreated: { length: createdLength }, cachedUpdated: { length: updatedLength }, cachedDestroyed: { length: destroyedLength }, } = dataSet;
    const lengths = new Map([
        [RecordCachedType.selected, selectedLength],
        [RecordCachedType.add, createdLength],
        [RecordCachedType.update, updatedLength],
        [RecordCachedType.delete, destroyedLength],
    ]);
    lengths.forEach((length, key) => {
        if (length) {
            options.push(React.createElement(Option, { key: String(key), value: key }, $l('Table', cachedTypeIntlMap[key])));
            if (!defaultRecordCachedType || key === RecordCachedType.add) {
                defaultRecordCachedType = key;
            }
        }
        else {
            if (recordCachedType === key) {
                recordCachedType = undefined;
            }
            if (defaultRecordCachedType === key) {
                defaultRecordCachedType = undefined;
            }
        }
    });
    const optionsRenderer = useCallback(({ text, value }) => {
        return (React.createElement(React.Fragment, null,
            text,
            React.createElement("span", { className: `${prefixCls}-cached-type-count` }, getCount(dataSet, value))));
    }, [prefixCls, dataSet]);
    const handleChangeRecordCachedType = useCallback(action((newRecordCachedType) => {
        tableStore.recordCachedType = newRecordCachedType;
    }), [tableStore]);
    useEffect(action(() => {
        if (tableStore.recordCachedType !== recordCachedType) {
            tableStore.recordCachedType = recordCachedType;
        }
    }), [tableStore, recordCachedType]);
    useEffect(action(() => {
        if (tableStore.defaultRecordCachedType !== defaultRecordCachedType) {
            tableStore.defaultRecordCachedType = defaultRecordCachedType;
            if (defaultRecordCachedType === RecordCachedType.add) {
                tableStore.showCachedSelection = true;
            }
            else if (!defaultRecordCachedType) {
                tableStore.showCachedSelection = false;
            }
        }
    }), [tableStore, defaultRecordCachedType]);
    if (options.length) {
        return (React.createElement("span", null, $l('Table', 'cached_tips', {
            type: options.length > 1 ? (React.createElement(ObserverSelect, { className: `${prefixCls}-cached-type`, optionRenderer: optionsRenderer, isFlat: true, value: recordCachedType || defaultRecordCachedType, onChange: handleChangeRecordCachedType, size: Size.small, clearButton: false }, options)) : $l('Table', cachedTypeIntlMap[recordCachedType || defaultRecordCachedType || RecordCachedType.selected]),
            count: getCount(dataSet, recordCachedType || defaultRecordCachedType),
        })));
    }
    return null;
};
const ObserverCachedTips = observer(CachedTips);
const SelectionTips = function SelectionTips() {
    const { prefixCls, dataSet, tableStore, showSelectionCachedButton, onShowCachedSelectionChange } = useContext(TableContext);
    const { showCachedSelection } = tableStore;
    const getTitle = () => {
        return $l('Table', showCachedSelection ? 'hide_cached_records' : 'show_cached_records');
    };
    const cachedButton = showSelectionCachedButton && (dataSet.cacheSelectionKeys || dataSet.cacheModifiedKeys) && dataSet.cachedRecords.length > 0 ? (React.createElement(Tooltip, { title: getTitle },
        React.createElement(Button, { size: Size.small, className: `${prefixCls}-switch`, funcType: FuncType.flat, icon: showCachedSelection ? 'visibility_off' : 'visibility', onClick: action(() => {
                const newShowCachedSelection = !showCachedSelection;
                tableStore.showCachedSelection = newShowCachedSelection;
                if (onShowCachedSelectionChange) {
                    onShowCachedSelectionChange(newShowCachedSelection);
                }
            }) }))) : null;
    if (tableStore.showCachedTips) {
        return (React.createElement("div", { className: `${prefixCls}-selection-tips` },
            React.createElement(ObserverCachedTips, { dataSet: dataSet, tableStore: tableStore, prefixCls: prefixCls }),
            cachedButton));
    }
    return tableStore.showSelectionTips ? (React.createElement("div", { className: `${prefixCls}-selection-tips` },
        React.createElement("span", null, $l('Table', 'selection_tips', { count: getCount(dataSet, RecordCachedType.selected) })),
        cachedButton)) : cachedButton;
};
SelectionTips.displayName = 'SelectionTips';
export default observer(SelectionTips);
